/**
 * Admin Super-Powers — "Assign owner" modal (super_admin/content_moderator,
 * `venues:write`).
 *
 * Force-assign / reassign / clear a venue's owning B2B account. Composes
 * Modal + SegmentedControl (Assign vs Clear) + TextInput (account UUID) +
 * Textarea (reason ≥10). Client-side validation via the shared
 * `ReassignVenueOwnershipInputSchema` from @bravemove/types.
 *
 * The admin gets the account id from the "Create business" flow on the user
 * detail page (a plain UUID input is sufficient — no account search at MVP).
 *
 * 422 ACCOUNT_NOT_FOUND / ACCOUNT_NOT_ACTIVE (when an account id is supplied)
 * + 404 VENUE_NOT_FOUND surface inline. Gated by `venues:write` — the parent
 * page hides the trigger button for any actor lacking the permission.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  Modal,
  SegmentedControl,
  TextInput,
  Textarea,
} from '@bravemove/ui-web';
import {
  ReassignVenueOwnershipInputSchema,
  type ReassignVenueOwnershipInput,
} from '@bravemove/types';
import { ApiError } from '../../../auth/types';
import { translateApiError } from '../../../auth/errors.js';

export interface AssignOwnerDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The venue's current owner id (pre-fills the field in Assign mode). */
  readonly currentOwnerB2bAccountId: string | null;
  /** Wired to `adminVenuesApi.reassignOwner` by the parent (real or fixture). */
  readonly onSubmit: (input: ReassignVenueOwnershipInput) => Promise<void>;
  /** Called after a successful reassignment so the parent can refetch. */
  readonly onAssigned?: () => void;
}

type Mode = 'assign' | 'clear';

export function AssignOwnerDialog({
  open,
  onClose,
  currentOwnerB2bAccountId,
  onSubmit,
  onAssigned,
}: AssignOwnerDialogProps) {
  const { t } = useI18n();

  const [mode, setMode] = useState<Mode>('assign');
  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setMode('assign');
    setAccountId(currentOwnerB2bAccountId ?? '');
    setReason('');
    setSubmitting(false);
    setServerError(null);
  }, [open, currentOwnerB2bAccountId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(() => {
    const candidate = {
      newOwnerB2bAccountId: mode === 'clear' ? null : accountId.trim(),
      reason: reason.trim(),
    };
    return ReassignVenueOwnershipInputSchema.safeParse(candidate);
  }, [mode, accountId, reason]);

  const disableSubmit = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setServerError(null);
    setSubmitting(true);
    try {
      await onSubmit(validation.data);
      onAssigned?.();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ACCOUNT_NOT_FOUND') {
        setServerError(
          t('admin:f43fc.assignOwner.error_account_not_found', {
            defaultValue: 'No B2B account with that ID exists.',
          }),
        );
      } else if (err instanceof ApiError && err.code === 'ACCOUNT_NOT_ACTIVE') {
        setServerError(
          t('admin:f43fc.assignOwner.error_account_not_active', {
            defaultValue: 'That B2B account is not active. Resume it first.',
          }),
        );
      } else {
        setServerError(translateApiError(err, t));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:f43fc.assignOwner.title', { defaultValue: 'Assign venue owner' })}
      description={t('admin:f43fc.assignOwner.description', {
        defaultValue:
          'Force-assign the venue to a B2B account, or clear its owner. This is audit-logged.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="assign-owner-cancel"
          >
            {t('admin:common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant={mode === 'clear' ? 'destructive' : 'primary'}
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="assign-owner-submit"
          >
            {mode === 'clear'
              ? t('admin:f43fc.assignOwner.submit_clear', { defaultValue: 'Clear owner' })
              : t('admin:f43fc.assignOwner.submit_assign', { defaultValue: 'Assign owner' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="assign-owner-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField label={t('admin:f43fc.assignOwner.mode_label', { defaultValue: 'Action' })}>
          <div data-testid="assign-owner-mode">
            <SegmentedControl<Mode>
              value={mode}
              onChange={(v) => setMode(v)}
              options={[
                {
                  value: 'assign',
                  label: t('admin:f43fc.assignOwner.mode_assign', { defaultValue: 'Assign' }),
                },
                {
                  value: 'clear',
                  label: t('admin:f43fc.assignOwner.mode_clear', { defaultValue: 'Clear owner' }),
                },
              ]}
              aria-label={t('admin:f43fc.assignOwner.mode_label', { defaultValue: 'Action' })}
            />
          </div>
        </FormField>

        {mode === 'assign' ? (
          <FormField
            label={t('admin:f43fc.assignOwner.account_label', { defaultValue: 'B2B account ID' })}
            helper={t('admin:f43fc.assignOwner.account_helper', {
              defaultValue: 'The UUID of the owning business account.',
            })}
            required
          >
            <TextInput
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              data-testid="assign-owner-account"
              aria-label={t('admin:f43fc.assignOwner.account_label', {
                defaultValue: 'B2B account ID',
              })}
            />
          </FormField>
        ) : (
          <div data-testid="assign-owner-clear-note">
            <Banner tone="warning">
              {t('admin:f43fc.assignOwner.clear_note', {
                defaultValue: 'This removes the venue’s owner — it becomes unclaimed.',
              })}
            </Banner>
          </div>
        )}

        <FormField
          label={t('admin:f43fc.assignOwner.reason_label', { defaultValue: 'Reason' })}
          helper={t('admin:f43fc.assignOwner.reason_helper', {
            defaultValue: 'Visible to other admins. 10–500 characters.',
          })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="assign-owner-reason"
            aria-label={t('admin:f43fc.assignOwner.reason_label', { defaultValue: 'Reason' })}
          />
        </FormField>

        {serverError ? (
          <div data-testid="assign-owner-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default AssignOwnerDialog;
